// BulkInviteUser.jsx

import React, {
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import Papa from 'papaparse';

import { roleOptions } from '../../static/common';

const BulkInviteUser = forwardRef(
  (
    {
      handleSubmit,
      loading,
      inviteResults = null,
      resetResults,
    },
    ref,
  ) => {
    const [users, setUsers] = useState([]);
    const [error, setError] =
      useState('');
    const [fileName, setFileName] =
      useState('');

    const showResultsOnly = !!inviteResults;

    const isInviteDisabled =
      loading ||
      users.length === 0 ||
      !!error;

    const handleFileUpload = (e) => {
      setError('');

      const file = e.target.files[0];

      if (!file) return;

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: 'UTF-8',

        complete(results) {
          const data = results.data;

          if (
            !data ||
            data.length === 0
          ) {
            setError(
              'The uploaded file is empty. Please upload a valid CSV file.',
            );

            setUsers([]);
            setFileName('');

            return;
          }

          const parsedUsers = [];
          const seenEmails =
            new Set();

          let hasDuplicates = false;

          for (const row of data) {
            const firstName =
              row[
                'first name'
              ]?.trim();

            const email = row[
              'email'
            ]
              ?.trim()
              .toLowerCase();

            const inputRole =
              row['role']
                ?.trim()
                .toLowerCase();

            const matchedRole =
              roleOptions.find(
                (r) =>
                  r.label.toLowerCase() ===
                  inputRole,
              );

            if (
              !firstName ||
              !email ||
              !matchedRole
            ) {
              setError(
                'The uploaded file contains some invalid or missing values.',
              );

              setUsers([]);
              setFileName('');

              return;
            }

            if (
              seenEmails.has(email)
            ) {
              hasDuplicates = true;
              continue;
            }

            seenEmails.add(email);

            parsedUsers.push({
              firstName,
              lastName:
                row[
                  'last name'
                ]?.trim() || '',
              email,
              role:
                matchedRole.value,
            });
          }

          if (
            parsedUsers.length > 10
          ) {
            setError(
              'You can only invite up to 10 users at a time.',
            );

            setUsers([]);
            setFileName('');

            return;
          }

          if (hasDuplicates) {
            setError(
              'Duplicate emails found. Only unique emails were included.',
            );
          }

          setUsers(parsedUsers);
          setFileName(file.name);
        },
      });
    };

    const handleSampleDownload =
      () => {
        const sample = [
          [
            'first name',
            'last name',
            'email',
            'role',
          ],
          [
            'John',
            'Doe',
            'john@example.com',
            'Org Member',
          ],
          [
            'Jane',
            'Smith',
            'jane@example.com',
            'Org Manager',
          ],
        ];

        const csvContent = sample
          .map((e) =>
            e.join(','),
          )
          .join('\n');

        const blob = new Blob(
          [csvContent],
          {
            type: 'text/csv;charset=utf-8;',
          },
        );

        const link =
          document.createElement(
            'a',
          );

        link.href =
          URL.createObjectURL(
            blob,
          );

        link.download =
          'sample_bulk_invite.csv';

        link.click();
      };

    const handleTryAgain =
      () => {
        setUsers([]);
        setError('');
        setFileName('');

        resetResults();
      };

    const handleSubmitForm =
      () => {
        handleSubmit(users);

        setError('');
        setFileName('');
        setUsers([]);
      };

    useImperativeHandle(ref, () => ({
      submit: handleSubmitForm,
      reset: handleTryAgain,
      isDisabled: isInviteDisabled,
    }));

    return (
      <Grid container spacing={2}>
        {showResultsOnly ? (
          <>
            <Grid item xs={12}>
              <Alert severity="success">
                {inviteResults
                  ?.invited.length ||
                  0}{' '}
                user(s) invited
                successfully.
              </Alert>
            </Grid>

            {inviteResults?.failed
              .length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    Some users could not
                    be invited.
                  </Alert>

                  <List dense>
                    {inviteResults.failed.map(
                      (
                        fail,
                        index,
                      ) => (
                        <ListItem
                          key={index}
                        >
                          <ListItemText
                            primary={
                              fail.email
                            }
                            secondary={
                              fail.reason
                            }
                          />
                        </ListItem>
                      ),
                    )}
                  </List>
                </Grid>
              )}
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="flex-end"
              >
                <Button
                  onClick={
                    handleSampleDownload
                  }
                >
                  Download Sample
                  CSV
                </Button>
              </Box>

              <Button
                fullWidth
                component="label"
                variant="outlined"
                disabled={loading}
                sx={{ mt: 1 }}
              >
                {fileName
                  ? 'Replace File'
                  : 'Upload CSV File'}

                <input
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={
                    handleFileUpload
                  }
                />
              </Button>
            </Grid>

            {fileName && (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Uploaded file:{' '}
                  {fileName}
                </Typography>
              </Grid>
            )}

            {error && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {error}
                </Alert>
              </Grid>
            )}
          </>
        )}
      </Grid>
    );
  },
);

export default BulkInviteUser;